// frontend/src/pages/Customer/CustomerList.jsx
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Building,
  CheckCircle,
  CreditCard,
  Edit,
  Eye,
  Filter,
  Globe,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  User,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./CustomersList.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  if (company.currency && typeof company.currency === "object" && company.currency.code) {
    return company.currency.code;
  }
  if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
    return company.currency;
  }
  return company.currency_code || company.base_currency || "USD";
};

const customerCode = (value) => `CUS-${String(value || 0).padStart(4, "0")}`;

const typeLabel = (value) => {
  if (value === "walk_in") return "Walk In";
  if (value === "credit") return "Credit";
  return "Regular";
};

export default function CustomerList() {
  const { currentUser, userType, company } = useAuth();
  const { getCurrencySymbol } = useCurrency();
  const navigate = useNavigate();
  const isSystemAdmin =
    userType === "system" && ["developer", "admin"].includes(currentUser?.role);

  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [status, setStatus] = useState("active");
  const [customerType, setCustomerType] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [viewCustomer, setViewCustomer] = useState(null);

  const formatCustomerCurrency = (amount, currencyInput) => {
    let currencyCode = "USD";
    if (typeof currencyInput === "string" && /^[A-Z]{3}$/.test(currencyInput)) {
      currencyCode = currencyInput;
    } else if (currencyInput && typeof currencyInput === "object" && currencyInput.code) {
      currencyCode = currencyInput.code;
    } else if (currencyInput && typeof currencyInput === "string") {
      currencyCode = currencyInput;
    }
    const num = Number(amount || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
    } catch {
      return `${currencyCode} ${num.toFixed(2)}`;
    }
  };

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${url}/api/customer`, {
        headers: getAuthHeaders(),
        params: {
          status,
          customer_type: customerType,
          search,
          ...(isSystemAdmin && selectedCompany
            ? { company_id: selectedCompany }
            : {}),
          limit: 500,
        },
      });
      setCustomers(response.data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(fetchCustomers, 200);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, status, customerType, search]);

  useEffect(() => {
    if (!isSystemAdmin) return;
    const loadCompanies = async () => {
      try {
        const response = await axios.get(`${url}/api/company`, {
          headers: getAuthHeaders(),
        });
        setCompanies(response.data?.data || []);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load companies");
      }
    };
    loadCompanies();
  }, [isSystemAdmin]);

  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const stats = useMemo(() => {
    const active = customers.filter((item) => item.status === "active").length;
    const creditCustomers = customers.filter(
      (item) => item.customer_type === "credit",
    ).length;
    const openingReceivables = customers.reduce(
      (sum, item) =>
        sum +
        (item.opening_balance_side === "debit"
          ? Number(item.opening_balance || 0)
          : 0),
      0,
    );
    const advances = customers.reduce(
      (sum, item) =>
        sum +
        (item.opening_balance_side === "credit"
          ? Number(item.opening_balance || 0)
          : 0),
      0,
    );
    const creditLimits = customers.reduce(
      (sum, item) => sum + Number(item.credit_limit || 0),
      0,
    );
    return {
      total: customers.length,
      active,
      creditCustomers,
      openingReceivables,
      advances,
      creditLimits,
    };
  }, [customers]);

  const deactivateCustomer = async (customer) => {
    if (
      !window.confirm(
        `Deactivate ${customer.customer_name}? The record and history will remain in the database.`,
      )
    ) {
      return;
    }
    try {
      const response = await axios.delete(`${url}/api/customer/${customer._id}`, {
        headers: getAuthHeaders(),
      });
      toast.success(response.data?.message || "Customer deactivated");
      await fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to deactivate customer");
    }
  };

  return (
    <main className="customer-list-page">
      <header className="customer-list-header">
        <div>
          <p className="customer-list-eyebrow">Sales</p>
          <h1>Customers</h1>
          <p>
            {isSystemAdmin ? <Globe size={14} /> : <Building size={14} />}
            {isSystemAdmin
              ? "Customer master across companies"
              : company?.company_name || "Company customers"}
          </p>
        </div>
        <div className="customer-list-header__actions">
          <button
            type="button"
            className="customer-list-button customer-list-button--secondary"
            onClick={() => setShowFilters((current) => !current)}
          >
            <Filter size={16} /> Filters
          </button>
          <button
            type="button"
            className="customer-list-button customer-list-button--primary"
            onClick={() => navigate("/AddCustomer")}
          >
            <Plus size={16} /> Add customer
          </button>
        </div>
      </header>

      <section className="customer-list-stats" aria-label="Customer statistics">
        <article><Users size={20} /><div><strong>{stats.total}</strong><span>Total customers</span></div></article>
        <article><CheckCircle size={20} /><div><strong>{stats.active}</strong><span>Active</span></div></article>
        <article><CreditCard size={20} /><div><strong>{stats.creditCustomers}</strong><span>Credit customers</span></div></article>
        <article>
          <span className="customer-list-stat-symbol">
            {getCurrencySymbol(companyCurrency)}
          </span>
          <div>
            <strong>{formatCustomerCurrency(stats.openingReceivables, companyCurrency)}</strong>
            <span>Opening receivables</span>
          </div>
        </article>
        <article>
          <span className="customer-list-stat-symbol">
            {getCurrencySymbol(companyCurrency)}
          </span>
          <div>
            <strong>{formatCustomerCurrency(stats.advances, companyCurrency)}</strong>
            <span>Customer advances</span>
          </div>
        </article>
        <article>
          <CreditCard size={20} />
          <div>
            <strong>{formatCustomerCurrency(stats.creditLimits, companyCurrency)}</strong>
            <span>Total credit limits</span>
          </div>
        </article>
      </section>

      {showFilters && (
        <section className="customer-list-filters">
          {isSystemAdmin && (
            <label>
              Company
              <select
                value={selectedCompany}
                onChange={(event) => setSelectedCompany(event.target.value)}
              >
                <option value="">All companies</option>
                {companies.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.company_name}{item.company_code ? ` (${item.company_code})` : ""}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            Status
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All statuses</option>
            </select>
          </label>
          <label>
            Customer type
            <select
              value={customerType}
              onChange={(event) => setCustomerType(event.target.value)}
            >
              <option value="all">All types</option>
              <option value="walk_in">Walk In</option>
              <option value="regular">Regular</option>
              <option value="credit">Credit</option>
            </select>
          </label>
        </section>
      )}

      <section className="customer-list-workspace">
        <div className="customer-list-toolbar">
          <div className="customer-list-search">
            <Search size={17} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search customer, ID, phone, email, address or company"
            />
            {search && (
              <button type="button" onClick={() => setSearch("")} aria-label="Clear search">
                <X size={16} />
              </button>
            )}
          </div>
          <button
            type="button"
            className="customer-list-refresh"
            onClick={fetchCustomers}
            aria-label="Refresh customers"
          >
            <RefreshCw size={17} />
          </button>
        </div>

        <div className="customer-list-table-wrap">
          <table className="customer-list-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Type</th>
                <th>Contact</th>
                <th>Address</th>
                <th>Opening balance</th>
                <th>Credit limit</th>
                {isSystemAdmin && <th>Company</th>}
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={isSystemAdmin ? 10 : 9}>
                    <div className="customer-list-empty">
                      <Loader2 className="customer-list-spin" size={23} /> Loading customers…
                    </div>
                  </td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan={isSystemAdmin ? 10 : 9}>
                    <div className="customer-list-empty">
                      <User size={24} /> No customers found
                    </div>
                  </td>
                </tr>
              ) : (
                customers.map((customer) => {
                  // ✅ Use company currency for all list display
                  const currency = companyCurrency;
                  const side = customer.opening_balance_side === "credit" ? "CR" : "DR";
                  return (
                    <tr key={customer._id}>
                      <td>
                        <div className="customer-list-name-cell">
                          <strong>{customer.customer_name}</strong>
                          <span>{customerCode(customer.customer_id)}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`customer-list-type customer-list-type--${customer.customer_type}`}>
                          {typeLabel(customer.customer_type)}
                        </span>
                      </td>
                      <td>
                        <div className="customer-list-contact">
                          {customer.phone && <span><Phone size={13} /> {customer.phone}</span>}
                          {customer.email && <span><Mail size={13} /> {customer.email}</span>}
                          {!customer.phone && !customer.email && <span>—</span>}
                        </div>
                      </td>
                      <td>
                        <span className="customer-list-address">
                          {customer.address ? <><MapPin size={13} /> {customer.address}</> : "—"}
                        </span>
                      </td>
                      <td className="customer-list-money">
                        {formatCustomerCurrency(customer.opening_balance, currency)} {Number(customer.opening_balance || 0) ? side : ""}
                      </td>
                      <td className="customer-list-money">
                        {formatCustomerCurrency(customer.credit_limit, currency)}
                      </td>
                      {isSystemAdmin && (
                        <td>{customer.company_id?.company_name || "—"}</td>
                      )}
                      <td>
                        <span className={`customer-list-status customer-list-status--${customer.status}`}>
                          {customer.status}
                        </span>
                      </td>
                      <td>{customer.created_at ? new Date(customer.created_at).toLocaleDateString() : "—"}</td>
                      <td>
                        <div className="customer-list-actions">
                          <button type="button" onClick={() => setViewCustomer(customer)} title="View details">
                            <Eye size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate(`/customer/edit/${customer._id}`)}
                            title={customer.status === "inactive" ? "Edit or reactivate" : "Edit customer"}
                          >
                            <Edit size={15} />
                          </button>
                          <button
                            type="button"
                            className="customer-list-danger"
                            onClick={() => deactivateCustomer(customer)}
                            disabled={customer.status === "inactive"}
                            title="Deactivate customer"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <footer className="customer-list-footer">
          Showing {customers.length} customer{customers.length === 1 ? "" : "s"}
        </footer>
      </section>

      {viewCustomer && (
        <div className="customer-view-backdrop" role="presentation" onMouseDown={() => setViewCustomer(null)}>
          <section
            className="customer-view-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="customer-view-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="customer-view-close"
              onClick={() => setViewCustomer(null)}
              aria-label="Close customer details"
            >
              <X size={18} />
            </button>
            <h2 id="customer-view-title">Customer details</h2>
            <div className="customer-view-grid">
              <div><span>Customer</span><strong>{viewCustomer.customer_name}</strong></div>
              <div><span>Customer ID</span><strong>{customerCode(viewCustomer.customer_id)}</strong></div>
              <div><span>Type</span><strong>{typeLabel(viewCustomer.customer_type)}</strong></div>
              <div><span>Status</span><strong>{viewCustomer.status}</strong></div>
              <div><span>Phone</span><strong>{viewCustomer.phone || "—"}</strong></div>
              <div><span>Email</span><strong>{viewCustomer.email || "—"}</strong></div>
              <div><span>Address</span><strong>{viewCustomer.address || "—"}</strong></div>
              <div><span>Currency</span><strong>{viewCustomer.currency_code || companyCurrency}</strong></div>
              <div>
                <span>Opening balance</span>
                <strong>
                  {formatCustomerCurrency(viewCustomer.opening_balance, companyCurrency)}
                  {" "}
                  {Number(viewCustomer.opening_balance || 0) ? (viewCustomer.opening_balance_side === "credit" ? "CR" : "DR") : ""}
                </strong>
              </div>
              <div><span>Credit limit</span><strong>{formatCustomerCurrency(viewCustomer.credit_limit, companyCurrency)}</strong></div>
              <div><span>Opening date</span><strong>{viewCustomer.opening_balance_date ? new Date(viewCustomer.opening_balance_date).toLocaleDateString() : "—"}</strong></div>
              <div><span>Reference</span><strong>{viewCustomer.opening_balance_reference || "—"}</strong></div>
              <div><span>Company</span><strong>{viewCustomer.company_id?.company_name || company?.company_name || "—"}</strong></div>
              <div><span>Created</span><strong>{viewCustomer.created_at ? new Date(viewCustomer.created_at).toLocaleString() : "—"}</strong></div>
              <div><span>Created by</span><strong>{viewCustomer.created_by?.name || "System"}</strong></div>
              <div><span>Updated</span><strong>{viewCustomer.updated_at ? new Date(viewCustomer.updated_at).toLocaleString() : "—"}</strong></div>
            </div>
            <p className="customer-view-note">
              Customer creation and editing do not post the Chart of Accounts. A debit opening balance means the customer owes the company; a credit opening balance means the customer has an advance or overpayment.
            </p>
          </section>
        </div>
      )}
    </main>
  );
}